//@flow
import React, {useEffect, useMemo, useState} from 'react';
import CustomModal from '../CustomModal/CustomModal';
import {
  Checkbox,
  CircularProgress,
  FormControlLabel,
  InputBase,
  Typography,
  makeStyles,
} from '@material-ui/core';
import CustomButton from '../CustomButton/CustomButton';
import {colors} from '../../assets/styles/colors';
import {Search} from '@material-ui/icons';
import {useCategories} from '../../modules/categories/utils';
import {
  useSubCategories,
  useSubCategoriesActions,
} from '../../modules/sub-categories/utils';
import {useDebounceEffect} from '../../utils/react';
import {useSearchMinimalProducts} from '../../modules/products/utils/hooks';
import {getImageUrl} from '../../utils/image_provider';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    width: '90vw',
    height: '80vh',
    maxWidth: '550px',
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    gap: '.75rem',
  },
  title: {
    textAlign: 'center',
    fontWeight: 600,
    fontSize: '1.25rem',
  },
  resourcesList: {
    display: 'flex',
    gap: '.5rem',
    alignItems: 'center',
  },
  resourceButton: {
    borderRadius: '.5rem',
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  footerLabel: {
    fontSize: '.875rem',
    fontWeight: 600,
  },
  footerButton: {
    paddingInline: '1.5rem',
  },
  formControlLabel: {
    width: '100%',
    borderBottom: `1px solid ${colors.grey300}`,
    margin: 0,
    padding: '.5rem',

    '&:last-child': {
      borderBottom: 'none',
    },
  },
  resultList: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    minHeight: '300px',
    overflowY: 'auto',
    overflowX: 'hidden',
  },
  labelContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '.5rem',

    width: '100%',

    '&:hover': {
      color: colors.primary,
    },
  },
  labelPicture: {
    width: '2.5rem',
    height: '2.5rem',
    borderRadius: '.5rem',
    overflow: 'hidden',

    '& img': {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
    },
  },
  labelName: {
    fontSize: '.875rem',
    fontWeight: 600,
    flexGrow: 1,
  },
  labelRight: {
    fontSize: '.875rem',
    color: colors.grey500,
  },
  searchInput: {
    border: 'none',
    borderRadius: '1.5rem',
    padding: '.5rem 1rem',
    gap: '.5rem',
    fontSize: '.875rem',
    background: colors.grey300,
  },
  loading: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
}));

type ResourceType = 'PRODUCTS' | 'CATEGORIES' | 'SUBCATEGORIES';

type ResourceOption = {
  label: string,
  value: ResourceType,
};

export type Resource = {
  id: string,
  label: string,
  secondaryLabel?: string,
  thumbnail?: string,
  type: ResourceType,
};

type SubmitResource = {
  resources: Resource[],
  type: ResourceType,
};
type Props = {
  open: boolean,
  onClose: () => void,
  limitResources?: number,
  availableResourceType?: ResourceType[],
  onSubmit: (params: SubmitResource) => void,
  defaultSelected?: {
    resources: Resource[],
    type: ResourceType,
  },
  resources?: Resource[],
};

const RESOURCE_OPTION: ResourceOption[] = [
  {
    label: 'Productos',
    value: 'PRODUCTS',
  },
  {
    label: 'Categorías',
    value: 'CATEGORIES',
  },
  {
    label: 'Subcategorías',
    value: 'SUBCATEGORIES',
  },
];

export const getSingularResourceType = (type: ResourceType) => {
  if (type === 'PRODUCTS') {
    return 'PRODUCT';
  }
  if (type === 'CATEGORIES') {
    return 'CATEGORY';
  }

  return 'SUBCATEGORY';
};

export default function RelatedResourcesModal(props: Props) {
  const {
    onClose,
    open,
    onSubmit,
    defaultSelected,
    limitResources = 5,
    availableResourceType,
    resources: propResources,
  } = props;

  const classes = useStyles();

  const categories = useCategories();
  const {
    subcategoriesFilter: subcategories,
    isFetchingSubcategories,
  } = useSubCategories();

  const {searchSubCategories, fetchSubCategories} = useSubCategoriesActions();
  const {
    loading: loadingSearchProducts,
    products,
    searchProductsByName,
  } = useSearchMinimalProducts({
    initialList: true,
  });

  const [searchKeyword, setSearchKeyword] = useState('');

  const [resourcesSelected, setResourcesSelected] = useState<Resource[]>(
    defaultSelected?.resources || []
  );

  const resourceOptions = useMemo(() => {
    if (!availableResourceType) return RESOURCE_OPTION;
    return RESOURCE_OPTION.filter(resource =>
      availableResourceType.includes(resource.value)
    );
  }, [availableResourceType]);

  const [
    resourceOptionSelected,
    setResourceOptionSelected,
  ] = useState<ResourceOption>(
    resourceOptions.find(
      resource => resource.value === defaultSelected?.type
    ) || resourceOptions[0]
  );

  useEffect(() => {
    if (!propResources) return;
    setResourcesSelected(propResources);

    setResourceOptionSelected(
      resourceOptions.find(
        resource => resource.value === propResources[0]?.type
      ) || resourceOptions[0]
    );
  }, [propResources, resourceOptions]);

  useDebounceEffect(
    () => {
      if (searchKeyword.length === 0) return;
      if (resourceOptionSelected.value === 'SUBCATEGORIES') {
        searchSubCategories(searchKeyword);
      }

      if (resourceOptionSelected.value === 'PRODUCTS') {
        searchProductsByName(searchKeyword);
      }
    },
    750,
    [searchKeyword]
  );

  useEffect(() => {
    fetchSubCategories();
  }, [fetchSubCategories]);

  const categoryResources: Resource[] = useMemo(() => {
    return categories.map(category => ({
      id: category.id,
      label: category.name,
      type: 'CATEGORIES',
    }));
  }, [categories]);

  const subcategoriesResource: Resource[] = useMemo(() => {
    return subcategories.map(promotion => ({
      id: promotion.id,
      label: promotion.name,
      type: 'SUBCATEGORIES',
    }));
  }, [subcategories]);

  const productsResource: Resource[] = useMemo(() => {
    return products.map(product => ({
      id: product.id,
      label: product.name,
      thumbnail: getImageUrl(product.mainImageId),
      type: 'PRODUCTS',
    }));
  }, [products]);

  const resources: Resource[] = useMemo(() => {
    if (resourceOptionSelected.value === 'CATEGORIES') {
      return categoryResources;
    }

    if (resourceOptionSelected.value === 'SUBCATEGORIES') {
      return subcategoriesResource;
    }

    if (resourceOptionSelected.value === 'PRODUCTS') {
      return productsResource;
    }

    return [];
  }, [
    resourceOptionSelected.value,
    categoryResources,
    subcategoriesResource,
    productsResource,
  ]);

  const toggleResourceSelected = (resource: Resource) => {
    const exist = resourcesSelected.find(
      resourceSelected => resourceSelected.id === resource.id
    );

    if (exist) {
      setResourcesSelected(
        resourcesSelected.filter(
          resourceSelected => resourceSelected.id !== resource.id
        )
      );
    } else {
      setResourcesSelected([...resourcesSelected, resource]);
    }
  };

  const handleSubmit = () => {
    onSubmit({
      resources: resourcesSelected,
      type: resourceOptionSelected.value,
    });
  };

  const loading = isFetchingSubcategories || loadingSearchProducts;

  return (
    <CustomModal open={open} onClose={onClose}>
      <aside className={classes.root}>
        <header className={classes.header}>
          <Typography className={classes.title}>Recursos Similares</Typography>
          <div className={classes.resourcesList}>
            {resourceOptions.map(resource => (
              <CustomButton
                key={resource.value}
                color={
                  resourceOptionSelected.value === resource.value
                    ? 'primary'
                    : 'default'
                }
                variant="outlined"
                fullWidth
                className={classes.resourceButton}
                onClick={() => {
                  setResourceOptionSelected(resource);
                  setSearchKeyword('');
                  setResourcesSelected([]);
                }}
              >
                {resource.label}
              </CustomButton>
            ))}
          </div>
          <InputBase
            startAdornment={<Search />}
            placeholder={`Buscar ${resourceOptionSelected.label}`}
            className={classes.searchInput}
            variant="outlined"
            value={searchKeyword}
            onChange={e => setSearchKeyword(e.target.value)}
          />
        </header>
        <div className={classes.resultList}>
          {!loading &&
            resources.map(resource => {
              const checked = resourcesSelected.some(
                resourceSelected => resourceSelected.id === resource.id
              );

              const disabledMaxSelected =
                resourcesSelected.length >= limitResources && !checked;

              const disabled = loading || disabledMaxSelected;

              return (
                <FormControlLabel
                  key={`${resource.id}-${resource.type}`}
                  control={
                    <Checkbox
                      color="primary"
                      disabled={disabled}
                      onChange={() => toggleResourceSelected(resource)}
                      checked={checked}
                    />
                  }
                  className={classes.formControlLabel}
                  classes={{
                    label: classes.formControlLabel,
                  }}
                  label={
                    <div className={classes.labelContainer}>
                      {resource.thumbnail && (
                        <picture className={classes.labelPicture}>
                          <img src={resource.thumbnail} alt="" />
                        </picture>
                      )}

                      <Typography className={classes.labelName}>
                        {resource.label}
                      </Typography>

                      {resource.secondaryLabel && (
                        <Typography className={classes.labelRight}>
                          {resource.secondaryLabel}
                        </Typography>
                      )}
                    </div>
                  }
                ></FormControlLabel>
              );
            })}

          {loading && (
            <div className={classes.loading}>
              <CircularProgress size={24} />
            </div>
          )}
        </div>
        <footer className={classes.footer}>
          <Typography className={classes.footerLabel}>
            {resourcesSelected.length} recursos seleccionados
          </Typography>

          <CustomButton
            variant="contained"
            className={classes.footerButton}
            onClick={handleSubmit}
          >
            Agregar
          </CustomButton>
        </footer>
      </aside>
    </CustomModal>
  );
}
